import type { FormDetailResponse } from "@/types/entities/formality-request";
import type { ApiNode } from "@/workflow_api/types/core";
import { getLastFilledField } from "@/utils/filled-fields";

export const getIcon = ({ type }: ApiNode): string => {
  switch (type) {
    case "FORM":
      return "bars";
    case "SIGN_DOCUMENT":
      return "signature";
    default:
      return "";
  }
};

type FieldCorrection = { filled_field_id: string; status: string };

function findLatestFieldCorrection(
  groupCorrections: { field_corrections?: FieldCorrection[] }[],
  filledFields: { id: string }[],
): FieldCorrection | undefined {
  const allCorrections = groupCorrections.flatMap(
    (c) => c.field_corrections || [],
  );
  const filledFieldIds = new Set(filledFields.map((ff) => ff.id));

  for (let i = allCorrections.length - 1; i >= 0; i--) {
    if (filledFieldIds.has(allCorrections[i].filled_field_id)) {
      return allCorrections[i];
    }
  }
  return undefined;
}

export const getCorrections = (
  data: FormDetailResponse,
  revisionId: string,
  selectedGroupId: string | null,
) => {
  let count = 0;
  const corr = new Map<
    string,
    { status: "DENIED" | "APPROVED"; reason?: string }
  >();
  const lockedFields = new Set<string>();
  const nonPreventableFields = new Set<string>();

  // Verificar que form_assignments existe y tiene elementos
  if (!data?.form_assignments?.[0]?.revisions) {
    return { count: 0, corr, lockedFields, nonPreventableFields };
  }

  const current_revision = data.form_assignments[0].revisions.find(
    (rev) => rev.id === revisionId,
  );
  const group_corrections =
    current_revision?.corrections?.filter((c) => {
      return (
        c.group_id === selectedGroupId ||
        (c as unknown as { group: { id: string } }).group?.id ===
          selectedGroupId
      );
    }) || [];

  // En cada ciclo, TODOS los campos deben ser evaluados
  // Solo pre-seleccionar los campos que ya fueron aprobados antes

  data.steps.forEach((step) => {
    step.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const { filled_fields } = field;

        // Si el campo es condicional y no tiene valores, no contarlo
        // porque está oculto y no es necesario revisarlo
        const isConditionalField = (field as { is_conditional?: boolean })
          .is_conditional;
        if (
          isConditionalField &&
          (!filled_fields || filled_fields.length === 0)
        ) {
          return;
        }

        // Siempre contar todos los campos visibles, tengan o no valores
        count += 1;

        if (!filled_fields || filled_fields.length === 0) {
          // Los campos sin valores también se marcan como aprobados por defecto
          // Usar el ID del campo como identificador sintético
          corr.set(`field_${field.id}`, { status: "APPROVED" });
          return;
        }

        const latestFilledField = getLastFilledField(filled_fields);
        if (!latestFilledField) return;
        const latestFieldCorrection = findLatestFieldCorrection(
          group_corrections,
          filled_fields,
        );

        const wasApprovedBefore = latestFieldCorrection?.status === "APPROVED";
        if (
          typeof field.is_preventable === "boolean" &&
          !field.is_preventable
        ) {
          nonPreventableFields.add(latestFilledField.id);
        }
        if (wasApprovedBefore) {
          // Campo fue aprobado antes, mantenerlo aprobado y bloqueado
          lockedFields.add(latestFilledField.id);
          corr.set(latestFilledField.id, { status: "APPROVED" });
        } else {
          // Todos los demás campos se marcan como aprobados por defecto
        }
        corr.set(latestFilledField.id, { status: "APPROVED" });
      });
    });
  });

  return {
    count,
    corr,
    lockedFields,
    nonPreventableFields,
  };
};
